import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { safeStorage } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => safeStorage.getItem('apollo_token'));
  const [loading, setLoading] = useState(true);

  // Logout function - defined first so it can be used in fetchUser
  const logout = useCallback(() => {
    safeStorage.removeItem('apollo_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  }, []);

  const fetchUser = useCallback(async () => {
    const currentToken = safeStorage.getItem('apollo_token');
    if (!currentToken) {
      setLoading(false);
      return;
    }
    
    try {
      // Ensure the token is set in headers before making the request
      axios.defaults.headers.common['Authorization'] = `Bearer ${currentToken}`;
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      // Only logout if token is invalid (401), not for network errors
      if (error.response?.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  // Expired/invalid session on any authenticated call: sign out, explain, and send admin users to the login page
  useEffect(() => {
    const id = axios.interceptors.response.use(undefined, (error) => {
      const sentToken = (error.config?.headers?.Authorization || '').startsWith('Bearer ');
      const isAuthCall = /\/auth\/(login|refresh)/.test(error.config?.url || '');
      if (error.response?.status === 401 && sentToken && !isAuthCall && safeStorage.getItem('apollo_token')) {
        logout();
        toast.error('Your session has expired. Please sign in again.');
        if (window.location.pathname.startsWith('/admin')) window.location.assign('/login');
      }
      return Promise.reject(error);
    });
    return () => axios.interceptors.response.eject(id);
  }, [logout]);

  // Silent token refresh — runs every 4 hours to keep session alive
  useEffect(() => {
    const REFRESH_INTERVAL = 4 * 60 * 60 * 1000; // 4 hours
    const refresh = async () => {
      const t = safeStorage.getItem('apollo_token');
      if (!t) return;
      try {
        const res = await axios.post(`${API_URL}/auth/refresh`, null, {
          headers: { Authorization: `Bearer ${t}` },
        });
        if (res.data?.access_token) {
          safeStorage.setItem('apollo_token', res.data.access_token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.access_token}`;
          setToken(res.data.access_token);
        }
      } catch {
        // If refresh fails the existing token still works until expiry
      }
    };
    const id = setInterval(refresh, REFRESH_INTERVAL);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=') || window.location.pathname === '/auth/callback') {
      setLoading(false);
      return;
    }
    
    const currentToken = safeStorage.getItem('apollo_token');
    if (currentToken) {
      // Sync token state if it differs
      if (currentToken !== token) {
        setToken(currentToken);
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${currentToken}`;
      // Keep loading true until fetchUser completes
      setLoading(true);
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = async (email, password, rememberMe = false) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password, remember_me: rememberMe });
    if (response.data?.requires_2fa) return response.data;
    const { access_token, user: userData } = response.data;
    
    safeStorage.setItem('apollo_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  // Second step for accounts with authenticator MFA
  const completeMfa = async (mfaToken, code) => {
    const { data } = await axios.post(`${API_URL}/auth/login/mfa`, { mfa_token: mfaToken, code });
    safeStorage.setItem('apollo_token', data.access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
    setToken(data.access_token);
    setUser(data.user);
    return data.user;
  };

  // Swap in a fresh token (e.g. after a password change revokes older sessions)
  const updateToken = (t) => {
    if (!t) return;
    safeStorage.setItem('apollo_token', t);
    axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
    setToken(t);
  };

  const register = async (email, password, name, company = null) => {
    const response = await axios.post(`${API_URL}/auth/register`, { email, password, name, company });
    const { access_token, user: userData } = response.data;
    
    safeStorage.setItem('apollo_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = () => {
    try {
      const redirectUrl = window.location.origin + '/auth/callback';
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    } catch (error) {
      console.error('Failed to initiate Google login:', error);
      throw new Error('Google sign-in is not available. Please use email/password login or adjust your browser privacy settings.');
    }
  };

  const handleGoogleCallback = async (sessionId) => {
    try {
      // Set loading to true during the callback process
      setLoading(true);
      
      const response = await axios.post(`${API_URL}/auth/google/callback`, {
        session_id: sessionId
      });
      
      if (response.data?.requires_2fa) {
        setLoading(false);
        return response.data;
      }
      const { access_token, user: userData } = response.data;

      // Validate response before saving
      if (!access_token) {
        throw new Error('No access token received from server');
      }
      
      // Save token and user state atomically
      safeStorage.setItem('apollo_token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);
      setUser(userData);
      setLoading(false);
      
      return userData;
    } catch (error) {
      console.error('Google auth callback failed:', error);
      setLoading(false);
      throw error;
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    loginWithGoogle,
    handleGoogleCallback,
    logout,
    updateUser,
    updateToken,
    completeMfa,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
